import { Component, Input, SimpleChanges } from '@angular/core';
import { AiPresenceShareOfVoiceResponse, AiPresenceShareOfVoiceRowData } from '../types';
import { NoWidgetDataComponent } from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ClientSideRowModelModule, ColDef, EventApiModule, GridApi, GridOptions, GridReadyEvent, themeQuartz } from 'ag-grid-community';
import { ModuleRegistry } from 'ag-grid-community';
import { CellStyleModule } from 'ag-grid-community'; 
import { RowSelectionModule } from 'ag-grid-community';
import { TableHeaderCellComponent } from '../../../shared/components/table-header-cell/table-header-cell.component';
import { PerformanceTrendRendererComponent } from '../../../shared/components/performance-trend-renderer/performance-trend-renderer.component';
import { addPercent } from '../../../utils/numbers';
import { PieChartComponent } from '../../../shared/components/charts/content-category-breakdown/pie-chart.component';
import { BrandColors, ChartData } from '../../../shared/types/common';
import { BrandColorRendererComponent } from '../../../shared/components/brand-color-renderer/brand-color-renderer.component';
import { ColumnApiModule } from 'ag-grid-community'; 
import { RenderApiModule } from 'ag-grid-community'; 
import { SortClickEvent } from '../../../utils/sort-state';
import { ValueType } from '../../../shared/components/overview-tiles/types';

ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelModule,
  RenderApiModule,
  ColumnApiModule,
  RowSelectionModule,
  EventApiModule
]);

@Component({
  selector: 'app-ai-share-of-voice',
  imports: [
    CommonModule,
    LoaderComponent,
    NoWidgetDataComponent,
    AgGridAngular,
    PieChartComponent
  ],
  templateUrl: './ai-share-of-voice.component.html',
  styleUrl: './ai-share-of-voice.component.scss'
})
export class AiShareOfVoiceComponent {
  @Input() loading = false;
  @Input() data?: AiPresenceShareOfVoiceResponse;

  protected rowData?: AiPresenceShareOfVoiceRowData;
  protected chartData: ChartData[] = [];
  protected colors = [BrandColors.MY_BRAND, BrandColors.BRAND_1, BrandColors.BRAND_2, BrandColors.BRAND_3, BrandColors.BRAND_4, BrandColors.BRAND_5, BrandColors.BRAND_6, BrandColors.BRAND_7, BrandColors.BRAND_8, BrandColors.BRAND_9];
  protected gridApi?: GridApi;
  protected colDefs: ColDef[] = [];
  protected modules = [ClientSideRowModelModule];

  protected theme = themeQuartz.withParams({
    headerBackgroundColor: '#EBEDEF',
    rowHoverColor: 'rgba(233, 244, 245, 0.85)',
    rowHeight: 56,
    headerHeight: 46,
    fontSize: '14px',
    headerFontSize: '14px',
    fontFamily: 'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#4E5258',
    cellTextColor: '#4E5258',
    headerRowBorder: { style: 'solid', color: '#DEE0E4' },
    headerColumnBorder: { style: 'solid', color: '#DEE0E4' },
    rowBorder: { style: 'solid', color: '#DEE0E4' },
    columnBorder: { style: 'solid', color: '#DEE0E4' },
  });

  protected gridOptions: GridOptions = {
    suppressMovableColumns: true,
    defaultColDef: {
      sortable: true,
      resizable: false,
      filter: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    },
  };

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.addEventListener('sortClick' as any, this.onSortChanged.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.rowData = this.getRowData();
      this.colDefs = this.getColDef();
      this.chartData = this.getChartData();
    }
  }

  private getRowData(): AiPresenceShareOfVoiceRowData {
    return {
      brands: this.data?.brands.map(brand => {
        const { share, sentiment } = brand;
        const isSentimentInvalid = !sentiment;

        const shareContainerStyle = { display: 'flex', gap: '16px', justifyContent: 'center' };
        const sentimentContainerStyle = { display: 'flex', gap: '16px', justifyContent: isSentimentInvalid ? 'flex-start' : 'center', ...( isSentimentInvalid ? { paddingLeft: '8px' } : {}) };

        return {
          brand: brand.brand,
          share: {
            containerStyle: shareContainerStyle,
            value: addPercent(share?.value || 0),
            trend: share?.trend || 0,
            sortValue: share?.value || 0
          },
          sentiment: {
            containerStyle: sentimentContainerStyle,
            value: isSentimentInvalid ? ValueType.NA : addPercent(sentiment.value),
            trend: isSentimentInvalid ? ValueType.NA : sentiment.sentiment,
            sortValue: sentiment?.value || 0
          }
        }
      }) || []
    };
  }

  private getColDef(): ColDef[] {
    const valueStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '33px' };
    const trendStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '53px' };
    const trendConfig = { size: 'small' };
    const cellStyle = { fontWeight: '400' };
    return [
      {
        headerName: 'Brand',
        headerClass: 'brand-column',
        field: 'brand',
        width: 180,
        cellStyle: cellStyle,
        headerComponent: TableHeaderCellComponent,
        cellRenderer: BrandColorRendererComponent,
        cellRendererParams: {
          colors: this.colors
        }
      },
      {
        headerName: 'AI Share of Voice',
        field: 'share',
        width: 166,
        cellStyle: cellStyle,
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field: 'share',
          tableApi: this.gridApi
        },
        comparator: (valueA: any, valueB: any) => {
          const numA = valueA?.sortValue ?? 0;
          const numB = valueB?.sortValue ?? 0;
          return numA - numB;
        },
        cellRenderer: PerformanceTrendRendererComponent,
        cellRendererParams: {
          valueStyle: valueStyle,
          trendStyle: trendStyle,
          trendConfig: trendConfig
        }
      },
      {
        headerName: 'AI Sentiment',
        field: 'sentiment',
        width: 165,
        cellStyle: cellStyle,
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field: 'sentiment',
          tableApi: this.gridApi
        },
        comparator: (valueA: any, valueB: any) => {
          const numA = valueA?.sortValue ?? 0;
          const numB = valueB?.sortValue ?? 0;
          return numA - numB;
        },
        cellRenderer: PerformanceTrendRendererComponent,
        cellRendererParams: {
          valueStyle: valueStyle,
          trendStyle: trendStyle,
          trendConfig: trendConfig,
        },
      }
    ];
  }

  private getChartData(): ChartData[] {
    return this.chartData = this.data?.brands.map(brand => ({
      name: brand.brand,
      share: brand.share?.value || 0
    })) || [];
  }

  protected onSortChanged(event: SortClickEvent): void {
    if (!this.gridApi) return;

    const sortDirection = event.direction === 'asc' ? 'asc' : event.direction === 'desc' ? 'desc' : undefined;
    this.gridApi.applyColumnState({
      state: [{
        colId: event.field,
        sort: sortDirection
      }]
    });
    this.gridApi.refreshCells();
  }

}
